/**
 * 开发期种子数据。
 *
 * 硬性要求：字段名与可空性**逐字对齐** `src/api/types.ts`（← openapi.yaml）。
 * 一旦 mock 和契约漂移，前端会照着不存在的字段把 UI 写完，联调时才整片返工。
 * 因此这里显式声明为契约类型，让 tsc 在编译期充当第一道对账。
 *
 * 数据本身取自 docs/03-UIUX.md 的示例上下文（产品 aototokenpool 等），
 * 非「Acme / John Doe」这类占位物 —— 假数据长得越真，越早暴露真实排版问题。
 */
import type {
  Activation,
  AdminUser,
  ApiKey,
  CodeBatch,
  Customer,
  License,
  LicenseCode,
  Order,
  Plan,
  PlanSnapshot,
  Product,
  ProductVersion,
  Session,
  StatsOverview,
} from '../api/types';
import type { AuditEvent, SigningKey } from '../api/endpoints';

/** 固定时间锚点（epoch 秒）：让相对时间在演示中稳定可预期 */
export const NOW = Math.floor(Date.now() / 1000);
const DAY = 86_400;

export const MOCK_PRODUCTS: Product[] = [
  {
    id: '7c1f4a92-3b6e-4d21-9f08-5a2c8e1d4b60',
    slug: 'aototokenpool',
    name: 'AotoTokenPool',
    description: '多账号 Token 池调度器',
    is_active: true,
    revocation_list_version: 34,
    created_at: NOW - 412 * DAY,
    updated_at: NOW - 6 * DAY,
  },
  {
    id: 'a4e0d517-88c2-4f3a-b1d6-2e9f7c035a18',
    slug: 'shotpanel',
    name: 'ShotPanel',
    description: '截图批注与工单直连工具',
    is_active: true,
    revocation_list_version: 7,
    created_at: NOW - 233 * DAY,
    updated_at: NOW - 21 * DAY,
  },
  {
    id: 'd93b6c04-71af-4e58-8c17-6b4a0f2d9e33',
    slug: 'ledgerlens',
    name: 'LedgerLens',
    description: '本地账套差异比对',
    is_active: false,
    revocation_list_version: 2,
    created_at: NOW - 668 * DAY,
    updated_at: NOW - 190 * DAY,
  },
];

export const MOCK_PRODUCT_VERSIONS: ProductVersion[] = [
  {
    id: 'pv_01J8W0KQ7X9C1D-v1', product_id: MOCK_PRODUCTS[0]!.id, version: '2.4.1', channel: 'stable',
    release_notes: '修复 Token 池并发竞态；心跳上报改为批量', download_url: 'https://cdn.licenseharbor.dev/aototokenpool/2.4.1.zip',
    released_at: NOW - 30 * DAY, created_at: NOW - 30 * DAY,
  },
  {
    id: 'pv_01J8W0KQ7X9C1D-v2', product_id: MOCK_PRODUCTS[0]!.id, version: '2.5.0-beta.1', channel: 'beta',
    release_notes: '预览：多池加权调度', download_url: null,
    released_at: NOW - 3 * DAY, created_at: NOW - 3 * DAY,
  },
  {
    id: 'pv_01J8W0KQ7X9C1D-v3', product_id: MOCK_PRODUCTS[1]!.id, version: '1.8.0', channel: 'stable',
    release_notes: '截图批注支持多显示器', download_url: 'https://cdn.licenseharbor.dev/shotpanel/1.8.0.zip',
    released_at: NOW - 60 * DAY, created_at: NOW - 60 * DAY,
  },
  {
    id: 'pv_01J8W0KQ7X9C1D-v4', product_id: MOCK_PRODUCTS[2]!.id, version: '0.9.0', channel: 'stable',
    release_notes: null, download_url: null,
    released_at: NOW - 200 * DAY, created_at: NOW - 200 * DAY,
  },
];

export const MOCK_CUSTOMERS: Customer[] = [
  {
    id: 'c1a7f3d0-4b28-49e6-9d31-70f5c8a2b614',
    email: 'lu.wenqi@hexinsoft.cn',
    name: '陆文琦',
    phone: null,
    note: '年付续费，走对公转账',
    created_at: NOW - 300 * DAY,
  },
  {
    id: 'c2b8e4a1-5c39-4af7-8e42-81a6d9b3c725',
    email: 'ops@northgate-studio.io',
    name: 'Northgate Studio',
    phone: null,
    note: null,
    created_at: NOW - 118 * DAY,
  },
  {
    id: 'c3c9f5b2-6d4a-4b08-9f53-92b7eac4d836',
    email: null,
    name: null,
    phone: null,
    note: '发卡网站自助兑换，未登记',
    created_at: NOW - 44 * DAY,
  },
];

function snapshot(
  code: string,
  name: string,
  durationDays: number | null,
  maxDevices: number,
): PlanSnapshot {
  return {
    plan_id: `p-${code}`,
    plan_code: code,
    plan_name: name,
    duration_days: durationDays,
    max_devices: maxDevices,
    offline_grace_seconds: 259_200,
    version_constraint: '>=2.0.0 <3.0.0',
    entitlements: { concurrent_pools: maxDevices, priority_queue: true, export_csv: true },
    snapshot_at: NOW - 90 * DAY,
  };
}

const PRO_YEAR = snapshot('pro-year', '专业版年付', 365, 5);
const PRO_PERPETUAL = snapshot('pro-perpetual', '专业版永久', null, 3);
const TEAM_YEAR = snapshot('team-year', '团队版年付', 365, 20);

interface LicenseSeed {
  readonly idSuffix: string;
  readonly product: number;
  readonly plan: PlanSnapshot;
  readonly status: License['status'];
  readonly customer: number | null;
  readonly devices: number;
  readonly expiresInDays: number | null;
  readonly reason?: string;
}

const SEEDS: LicenseSeed[] = [
  { idSuffix: '9C1D', product: 0, plan: PRO_YEAR, status: 'active', customer: 0, devices: 3, expiresInDays: 214 },
  { idSuffix: '4E7A', product: 0, plan: PRO_YEAR, status: 'active', customer: 1, devices: 5, expiresInDays: 4 },
  { idSuffix: 'B028', product: 0, plan: PRO_PERPETUAL, status: 'active', customer: 2, devices: 1, expiresInDays: null },
  { idSuffix: '71F5', product: 1, plan: TEAM_YEAR, status: 'active', customer: 1, devices: 12, expiresInDays: 31 },
  {
    idSuffix: 'D3A9', product: 0, plan: PRO_YEAR, status: 'suspended', customer: 0, devices: 2,
    expiresInDays: 96, reason: '客户申请暂停，等待发票核销',
  },
  {
    idSuffix: '2C64', product: 1, plan: TEAM_YEAR, status: 'suspended', customer: null, devices: 0,
    expiresInDays: 148, reason: '疑似同一指纹跨授权复用，人工核查中',
  },
  {
    idSuffix: 'F80B', product: 0, plan: PRO_YEAR, status: 'revoked', customer: 2, devices: 0,
    expiresInDays: -12, reason: '授权码在论坛被公开分享',
  },
  {
    idSuffix: '5A17', product: 2, plan: PRO_PERPETUAL, status: 'revoked', customer: null, devices: 0,
    expiresInDays: null, reason: '退款后回收',
  },
  { idSuffix: 'E4B3', product: 0, plan: PRO_YEAR, status: 'expired', customer: 0, devices: 0, expiresInDays: -3 },
  { idSuffix: '06CF', product: 1, plan: TEAM_YEAR, status: 'expired', customer: 1, devices: 0, expiresInDays: -57 },
  { idSuffix: 'AA92', product: 0, plan: PRO_YEAR, status: 'active', customer: 1, devices: 5, expiresInDays: 6 },
  { idSuffix: '3D50', product: 1, plan: PRO_PERPETUAL, status: 'active', customer: 2, devices: 2, expiresInDays: null },
];

export const MOCK_LICENSES: License[] = SEEDS.map((seed, index) => {
  const product = MOCK_PRODUCTS[seed.product]!;
  const customer = seed.customer === null ? null : MOCK_CUSTOMERS[seed.customer]!;
  const issuedAt = NOW - (150 - index * 7) * DAY;
  return {
    id: `lic_01J8W${index.toString().padStart(2, '0')}KQ7X${seed.idSuffix}`,
    product_id: product.id,
    plan_id: seed.plan.plan_id,
    license_code_id: `code_01J8${index.toString().padStart(2, '0')}${seed.idSuffix}`,
    customer_id: customer?.id ?? null,
    order_id: customer === null ? null : `ORD-2026-${(1400 + index).toString()}`,
    status: seed.status,
    plan_snapshot: seed.plan,
    issued_at: issuedAt,
    expires_at: seed.expiresInDays === null ? null : NOW + seed.expiresInDays * DAY,
    revoked_at: seed.status === 'revoked' ? NOW - 9 * DAY : null,
    suspended_at: seed.status === 'suspended' ? NOW - 15 * DAY : null,
    status_reason: seed.reason ?? null,
    activated_device_count: seed.devices,
    note: null,
    created_at: issuedAt,
    updated_at: NOW - index * DAY,
  };
});

export const MOCK_STATS: StatsOverview = {
  licenses: { active: 1_284, suspended: 23, revoked: 47, expired: 316, expiring_in_7d: 19 },
  inventory: { available: 812, exported: 500, redeemed: 1_347, void: 62 },
  activations: { active_devices: 2_913, seen_24h: 1_106 },
  validations_24h: { total: 48_271, allow: 47_902, deny: 341, error: 28, p95_latency_ms: 38 },
  open_anomalies: 3,
};

/** 库存水位阈值（P-02 低于阈值的产品行前置警示图标） */
export const INVENTORY_LOW_WATERMARK = 200;

export interface InventoryRow {
  readonly product_id: string;
  readonly product_name: string;
  readonly available: number;
  readonly exported: number;
  readonly redeemed: number;
  readonly void: number;
  readonly low_watermark: number;
}

export const MOCK_INVENTORY: InventoryRow[] = [
  { product_id: MOCK_PRODUCTS[0]!.id, product_name: MOCK_PRODUCTS[0]!.name, available: 612, exported: 0, redeemed: 1_102, void: 3, low_watermark: INVENTORY_LOW_WATERMARK },
  { product_id: MOCK_PRODUCTS[1]!.id, product_name: MOCK_PRODUCTS[1]!.name, available: 143, exported: 0, redeemed: 208, void: 1, low_watermark: INVENTORY_LOW_WATERMARK },
  { product_id: MOCK_PRODUCTS[2]!.id, product_name: MOCK_PRODUCTS[2]!.name, available: 57, exported: 0, redeemed: 37, void: 0, low_watermark: INVENTORY_LOW_WATERMARK },
];

export const MOCK_PLANS: Plan[] = [
  {
    id: 'p-7c1f4a92-starter',
    product_id: '7c1f4a92-3b6e-4d21-9f08-5a2c8e1d4b60',
    code: 'starter',
    name: '入门版',
    duration_days: null,
    max_devices: 1,
    offline_grace_seconds: 259_200,
    version_constraint: null,
    entitlements: { concurrent_pools: 1, priority_queue: false, export_csv: true },
    is_active: true,
    created_at: NOW - 400 * DAY,
  },
  {
    id: 'p-7c1f4a92-pro',
    product_id: '7c1f4a92-3b6e-4d21-9f08-5a2c8e1d4b60',
    code: 'pro',
    name: '专业版',
    duration_days: 365,
    max_devices: 5,
    offline_grace_seconds: 259_200,
    version_constraint: '>=2.0.0',
    entitlements: { concurrent_pools: 5, priority_queue: true, export_csv: true },
    is_active: true,
    created_at: NOW - 300 * DAY,
  },
  {
    id: 'p-a4e0d517-team',
    product_id: 'a4e0d517-88c2-4f3a-b1d6-2e9f7c035a18',
    code: 'team',
    name: '团队版',
    duration_days: 365,
    max_devices: 20,
    offline_grace_seconds: 604_800,
    version_constraint: null,
    entitlements: { seats: 20, priority_queue: true, export_csv: true, audit_log: true },
    is_active: true,
    created_at: NOW - 200 * DAY,
  },
];

/* ── 激活设备（P-13）：跨授权设备表，含已解绑历史 ── */

/** 生成形似 sha256 的设备指纹（64 hex），仅演示用途 */
function fp(seed: number): string {
  return Array.from({ length: 64 }, (_, i) => '0123456789abcdef'[(seed * 7 + i * 13) % 16]).join('');
}

export const MOCK_ACTIVATIONS: Activation[] = [
  {
    id: 'act_01J8W0KQ7X9C1D-a1', license_id: 'lic_01J8W00KQ7X9C1D', product_id: MOCK_PRODUCTS[0]!.id,
    device_hash: fp(1), device_name: '陆文琦的 Windows 工作站', status: 'active',
    activated_at: NOW - 210 * DAY, last_seen_at: NOW - 40 * 60, deactivated_at: null, deactivated_by: null,
  },
  {
    id: 'act_01J8W0KQ7X9C1D-a2', license_id: 'lic_01J8W00KQ7X9C1D', product_id: MOCK_PRODUCTS[0]!.id,
    device_hash: fp(2), device_name: '陆文琦的 MacBook Pro', status: 'active',
    activated_at: NOW - 96 * DAY, last_seen_at: NOW - 3 * 3600, deactivated_at: null, deactivated_by: null,
  },
  {
    id: 'act_01J8W0KQ7X9C1D-a3', license_id: 'lic_01J8W00KQ7X9C1D', product_id: MOCK_PRODUCTS[0]!.id,
    device_hash: fp(3), device_name: '旧的测试机（已换机）', status: 'deactivated',
    activated_at: NOW - 300 * DAY, last_seen_at: NOW - 60 * DAY, deactivated_at: NOW - 58 * DAY, deactivated_by: 'user',
  },
  {
    id: 'act_01J8W01KQ7X4E7A-b1', license_id: 'lic_01J8W01KQ7X4E7A', product_id: MOCK_PRODUCTS[0]!.id,
    device_hash: fp(4), device_name: 'Northgate CI Runner-01', status: 'active',
    activated_at: NOW - 80 * DAY, last_seen_at: NOW - 12 * 60, deactivated_at: null, deactivated_by: null,
  },
  {
    id: 'act_01J8W01KQ7X4E7A-b2', license_id: 'lic_01J8W01KQ7X4E7A', product_id: MOCK_PRODUCTS[0]!.id,
    device_hash: fp(5), device_name: 'Northgate CI Runner-02', status: 'active',
    activated_at: NOW - 79 * DAY, last_seen_at: NOW - 5 * 60, deactivated_at: null, deactivated_by: null,
  },
  {
    id: 'act_01J8W01KQ7X4E7A-b3', license_id: 'lic_01J8W01KQ7X4E7A', product_id: MOCK_PRODUCTS[0]!.id,
    device_hash: fp(6), device_name: '疑似盗用的 VPS', status: 'deactivated',
    activated_at: NOW - 40 * DAY, last_seen_at: NOW - 20 * DAY, deactivated_at: NOW - 19 * DAY, deactivated_by: 'admin',
  },
  {
    id: 'act_01J8W02KQ7XB028-c1', license_id: 'lic_01J8W02KQ7XB028', product_id: MOCK_PRODUCTS[0]!.id,
    device_hash: fp(7), device_name: '发卡站兑付终端', status: 'active',
    activated_at: NOW - 30 * DAY, last_seen_at: NOW - 1 * 3600, deactivated_at: null, deactivated_by: null,
  },
  {
    id: 'act_01J8W03KQ7X71F5-d1', license_id: 'lic_01J8W03KQ7X71F5', product_id: MOCK_PRODUCTS[1]!.id,
    device_hash: fp(8), device_name: 'ShotPanel 设计工位-01', status: 'active',
    activated_at: NOW - 45 * DAY, last_seen_at: NOW - 22 * 60, deactivated_at: null, deactivated_by: null,
  },
  {
    id: 'act_01J8W03KQ7X71F5-d2', license_id: 'lic_01J8W03KQ7X71F5', product_id: MOCK_PRODUCTS[1]!.id,
    device_hash: fp(9), device_name: 'ShotPanel 设计工位-02', status: 'active',
    activated_at: NOW - 44 * DAY, last_seen_at: NOW - 90 * 60, deactivated_at: null, deactivated_by: null,
  },
  {
    id: 'act_01J8W10KQ7XAA92-e1', license_id: 'lic_01J8W10KQ7XAA92', product_id: MOCK_PRODUCTS[0]!.id,
    device_hash: fp(10), device_name: 'Northgate 笔记本', status: 'active',
    activated_at: NOW - 6 * DAY, last_seen_at: NOW - 4 * 3600, deactivated_at: null, deactivated_by: null,
  },
];

/* ── 订单（P-14）：关联客户与产品 ── */

export const MOCK_ORDERS: Order[] = [
  {
    id: 'ord_01J8W0KQ7X9C1D-o1', product_id: MOCK_PRODUCTS[0]!.id, customer_id: MOCK_CUSTOMERS[0]!.id,
    source: 'manual', external_order_no: 'ORD-2026-1401', amount_cents: 399_900, currency: 'CNY',
    note: '专业版年付（续费）', created_at: NOW - 300 * DAY,
  },
  {
    id: 'ord_01J8W0KQ7X9C1D-o2', product_id: MOCK_PRODUCTS[0]!.id, customer_id: MOCK_CUSTOMERS[0]!.id,
    source: 'manual', external_order_no: 'ORD-2026-1402', amount_cents: 399_900, currency: 'CNY',
    note: null, created_at: NOW - 214 * DAY,
  },
  {
    id: 'ord_01J8W0KQ7X9C1D-o3', product_id: MOCK_PRODUCTS[1]!.id, customer_id: MOCK_CUSTOMERS[1]!.id,
    source: 'card-site', external_order_no: 'PAY-88912', amount_cents: 1_299_00, currency: 'CNY',
    note: '团队版年付 20 席位', created_at: NOW - 118 * DAY,
  },
  {
    id: 'ord_01J8W0KQ7X9C1D-o4', product_id: MOCK_PRODUCTS[0]!.id, customer_id: MOCK_CUSTOMERS[2]!.id,
    source: 'card-site', external_order_no: null, amount_cents: null, currency: null,
    note: '发卡网站自助兑换，未登记客户', created_at: NOW - 44 * DAY,
  },
];

/* ── 授权码批次与库存（P-06/P-08） ── */

/**
 * mock 授权码掩码：对齐真实后端 maskLicenseCode 格式
 * `LH-XXXXX-•••••-•••••-•••••-XXXXX`（头 5 + 尾 5 可见，共 10 位，
 * 尾 5 位 28^5 ≈ 1700 万分之一碰撞，足以按明文定位作废）。
 */
export function codeMask(seed: number): string {
  const alphabet = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
  let body = '';
  for (let i = 0; i < 26; i += 1) body += alphabet[(seed * 13 + i * 7 + 3) % 28];
  const head = body.slice(0, 5);
  const tail = body.slice(-5);
  const middle = body.slice(5, -5);
  const dots = '•••••';
  const parts = [head];
  for (let i = 0; i + 5 <= middle.length; i += 5) parts.push(dots);
  parts.push(tail);
  return `LH-${parts.join('-')}`;
}

/** 从掩码反推可导出的明文：头 5 + 尾 5 已知，中间按 seed 确定性填充（掩码 = 明文脱敏）。 */
export function mockPlainFromMask(mask: string, seed: number): string {
  const alphabet = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
  const parts = mask.replace(/^LH-/, '').split('-');
  const head = parts[0];
  const tail = parts[parts.length - 1];
  let middle = '';
  for (let i = 0; i < 16; i += 1) middle += alphabet[(seed * 17 + i * 5 + 7) % 28];
  const body = `${head}${middle}${tail}`;
  const grouped = body.match(/.{1,5}/g)!.join('-');
  return `LH-${grouped}`;
}

export const MOCK_CODE_BATCHES: CodeBatch[] = [
  {
    id: 'cb_01J8W0KQ7X9C1D-b1', product_id: MOCK_PRODUCTS[0]!.id, plan_id: 'p-7c1f4a92-pro',
    batch_no: 'B2026-07-001', quantity: 200, note: '七月发卡站补货', export_count: 1,
    first_exported_at: NOW - 12 * DAY, exported_by: 'admin@licenseharbor.dev',
    stats: { available: 86, exported: 114, redeemed: 42, void: 0 }, created_at: NOW - 12 * DAY,
  },
  {
    id: 'cb_01J8W0KQ7X9C1D-b2', product_id: MOCK_PRODUCTS[0]!.id, plan_id: 'p-7c1f4a92-pro',
    batch_no: 'B2026-06-018', quantity: 100, note: null, export_count: 1,
    first_exported_at: NOW - 40 * DAY, exported_by: 'admin@licenseharbor.dev',
    stats: { available: 8, exported: 92, redeemed: 91, void: 1 }, created_at: NOW - 40 * DAY,
  },
  {
    id: 'cb_01J8W0KQ7X9C1D-b3', product_id: MOCK_PRODUCTS[1]!.id, plan_id: 'p-a4e0d517-team',
    batch_no: 'B2026-07-009', quantity: 80, note: 'Northgate 首批', export_count: 1,
    first_exported_at: NOW - 15 * DAY, exported_by: 'owner@licenseharbor.dev',
    stats: { available: 21, exported: 59, redeemed: 57, void: 2 }, created_at: NOW - 15 * DAY,
  },
  {
    id: 'cb_01J8W0KQ7X9C1D-b4', product_id: MOCK_PRODUCTS[1]!.id, plan_id: 'p-7c1f4a92-pro',
    batch_no: 'B2026-08-002', quantity: 50, note: '未导出，待复核', export_count: 0,
    first_exported_at: null, exported_by: null,
    stats: { available: 50, exported: 0, redeemed: 0, void: 0 }, created_at: NOW - 2 * DAY,
  },
];

export const MOCK_LICENSE_CODES: LicenseCode[] = [
  { id: 'code_01J8W0KQ7X9C1D-k1', product_id: MOCK_PRODUCTS[0]!.id, plan_id: 'p-7c1f4a92-pro', batch_id: 'cb_01J8W0KQ7X9C1D-b1', code_mask: codeMask(1), status: 'available', redeemed_at: null, void_reason: null, created_at: NOW - 12 * DAY },
  { id: 'code_01J8W0KQ7X9C1D-k2', product_id: MOCK_PRODUCTS[0]!.id, plan_id: 'p-7c1f4a92-pro', batch_id: 'cb_01J8W0KQ7X9C1D-b1', code_mask: codeMask(2), status: 'exported', redeemed_at: null, void_reason: null, created_at: NOW - 12 * DAY },
  { id: 'code_01J8W0KQ7X9C1D-k3', product_id: MOCK_PRODUCTS[0]!.id, plan_id: 'p-7c1f4a92-pro', batch_id: 'cb_01J8W0KQ7X9C1D-b1', code_mask: codeMask(3), status: 'redeemed', redeemed_at: NOW - 11 * DAY, void_reason: null, created_at: NOW - 12 * DAY },
  { id: 'code_01J8W0KQ7X9C1D-k4', product_id: MOCK_PRODUCTS[0]!.id, plan_id: 'p-7c1f4a92-pro', batch_id: 'cb_01J8W0KQ7X9C1D-b1', code_mask: codeMask(4), status: 'void', redeemed_at: null, void_reason: '批次作废：录入错误', created_at: NOW - 12 * DAY },
  { id: 'code_01J8W0KQ7X9C1D-k5', product_id: MOCK_PRODUCTS[0]!.id, plan_id: 'p-7c1f4a92-pro', batch_id: 'cb_01J8W0KQ7X9C1D-b2', code_mask: codeMask(5), status: 'redeemed', redeemed_at: NOW - 39 * DAY, void_reason: null, created_at: NOW - 40 * DAY },
  { id: 'code_01J8W0KQ7X9C1D-k6', product_id: MOCK_PRODUCTS[0]!.id, plan_id: 'p-7c1f4a92-pro', batch_id: 'cb_01J8W0KQ7X9C1D-b2', code_mask: codeMask(6), status: 'void', redeemed_at: null, void_reason: '公开分享，作废', created_at: NOW - 40 * DAY },
  { id: 'code_01J8W0KQ7X9C1D-k7', product_id: MOCK_PRODUCTS[1]!.id, plan_id: 'p-a4e0d517-team', batch_id: 'cb_01J8W0KQ7X9C1D-b3', code_mask: codeMask(7), status: 'redeemed', redeemed_at: NOW - 14 * DAY, void_reason: null, created_at: NOW - 15 * DAY },
  { id: 'code_01J8W0KQ7X9C1D-k8', product_id: MOCK_PRODUCTS[1]!.id, plan_id: 'p-7c1f4a92-pro', batch_id: 'cb_01J8W0KQ7X9C1D-b4', code_mask: codeMask(8), status: 'available', redeemed_at: null, void_reason: null, created_at: NOW - 2 * DAY },
];

/* ── 签名密钥（P-15）：按产品分组的 Ed25519 密钥生命周期 ── */

export const MOCK_SIGNING_KEYS: SigningKey[] = [
  {
    id: 'sk_01J8W0KQ7X9C1D-s1', key_id: '2025-aototokenpool-01', product_id: MOCK_PRODUCTS[0]!.id,
    algorithm: 'Ed25519', role: 'leaf', status: 'active',
    public_key_b64u: 'MCowBQYDK2VwAyEA_3jQkP4nY8aR9vXyLm2WnT7qB0cF1sH5dG6eJ8kL0mN',
    has_private_key: true,
    not_before: NOW - 320 * DAY, not_after: NOW + 45 * DAY, retired_at: null, created_at: NOW - 320 * DAY,
  },
  {
    id: 'sk_01J8W0KQ7X9C1D-s2', key_id: '2026-aototokenpool-01', product_id: MOCK_PRODUCTS[0]!.id,
    algorithm: 'Ed25519', role: 'leaf', status: 'retiring',
    public_key_b64u: 'MCowBQYDK2VwAyEA_9qWnT2sYzC7vXbLkM1fG5hJ8dR0cP3aE6tU9oI4yS',
    has_private_key: true,
    not_before: NOW - 200 * DAY, not_after: null, retired_at: null, created_at: NOW - 200 * DAY,
  },
  {
    id: 'sk_01J8W0KQ7X9C1D-s3', key_id: '2024-shotpanel-01', product_id: MOCK_PRODUCTS[1]!.id,
    algorithm: 'Ed25519', role: 'leaf', status: 'active',
    public_key_b64u: 'MCowBQYDK2VwAyEA_5mBxQ7vN2kLp9wR4tY6uI1oC3eZ8aD0fG7hJ2sK4nV',
    has_private_key: true,
    not_before: NOW - 230 * DAY, not_after: NOW + 135 * DAY, retired_at: null, created_at: NOW - 230 * DAY,
  },
  {
    id: 'sk_01J8W0KQ7X9C1D-s4', key_id: '2023-ledgerlens-01', product_id: MOCK_PRODUCTS[2]!.id,
    algorithm: 'Ed25519', role: 'leaf', status: 'retired',
    public_key_b64u: 'MCowBQYDK2VwAyEA_2qL8xW5nM0jK7pQ4tR9yU3iE6oA1sD5fG8hJ2kZ0cV',
    has_private_key: true,
    not_before: NOW - 668 * DAY, not_after: NOW - 190 * DAY, retired_at: NOW - 190 * DAY, created_at: NOW - 668 * DAY,
  },
  {
    id: 'sk_01J8W0KQ7X9C1D-s5', key_id: '2022-aototokenpool-01', product_id: MOCK_PRODUCTS[0]!.id,
    algorithm: 'Ed25519', role: 'leaf', status: 'compromised',
    public_key_b64u: 'MCowBQYDK2VwAyEA_7cF0sK2nH9vB4xR8tY1uO5pL3eW6qZ9iE0dG7hJ4mS',
    has_private_key: true,
    not_before: NOW - 700 * DAY, not_after: null, retired_at: null, created_at: NOW - 700 * DAY,
  },
  {
    id: 'sk_01J8W0KQ7X9C1D-s6', key_id: 'root-aototokenpool-01', product_id: MOCK_PRODUCTS[0]!.id,
    algorithm: 'Ed25519', role: 'root', status: 'active',
    public_key_b64u: 'MCowBQYDK2VwAyEA_1aB2cD3eF4gH5iJ6kL7mN8oP9qR0sT1uV2wX3yZ4aB5c',
    has_private_key: false,
    not_before: NOW - 700 * DAY, not_after: null, retired_at: null, created_at: NOW - 700 * DAY,
  },
];

/* ── 审计日志（P-18）：哈希链保护的只读流水 ── */

let auditPrevHash = '0'.repeat(64);
function auditEntry(entry: Omit<AuditEvent, 'prev_hash' | 'entry_hash' | 'result'> & { result?: 'success' | 'failure' }): AuditEvent {
  const entryHash = Array.from({ length: 64 }, (_, i) => '0123456789abcdef'[(entry.created_at + i * 29) % 16]).join('');
  const prev = auditPrevHash;
  auditPrevHash = entryHash;
  return { ...entry, result: entry.result ?? 'success', prev_hash: prev, entry_hash: entryHash };
}

export const MOCK_AUDIT_EVENTS: AuditEvent[] = [
  auditEntry({ id: 'ae_01', actor_type: 'admin', actor_id: 'usr_admin', product_id: MOCK_PRODUCTS[0]!.id, license_id: null, action: 'code_batch.create', target_type: 'code_batch', target_id: 'cb_01J8W0KQ7X9C1D-b4', reason: null, detail: { quantity: 50, plan_id: 'p-7c1f4a92-pro' }, ip: '203.0.113.10', user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)', created_at: NOW - 2 * DAY }),
  auditEntry({ id: 'ae_02', actor_type: 'admin', actor_id: 'usr_admin', product_id: MOCK_PRODUCTS[0]!.id, license_id: null, action: 'license.revoke', target_type: 'license', target_id: 'lic_01J8W05KQ7XF80B', reason: '授权码在论坛被公开分享', detail: null, ip: '203.0.113.10', user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)', created_at: NOW - 9 * DAY }),
  auditEntry({ id: 'ae_03', actor_type: 'admin', actor_id: 'usr_ops', product_id: MOCK_PRODUCTS[0]!.id, license_id: 'lic_01J8W04KQ7XD3A9', action: 'license.suspend', target_type: 'license', target_id: 'lic_01J8W04KQ7XD3A9', reason: '客户申请暂停，等待发票核销', detail: null, ip: '198.51.100.23', user_agent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)', created_at: NOW - 15 * DAY }),
  auditEntry({ id: 'ae_04', actor_type: 'admin', actor_id: 'usr_owner', product_id: MOCK_PRODUCTS[1]!.id, license_id: null, action: 'signing_key.rotate', target_type: 'signing_key', target_id: 'sk_01J8W0KQ7X9C1D-s2', reason: '例行轮换', detail: { new_kid: '2026-aototokenpool-01' }, ip: '203.0.113.10', user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)', created_at: NOW - 45 * DAY }),
  auditEntry({ id: 'ae_05', actor_type: 'system', actor_id: 'system', product_id: null, license_id: null, action: 'auth.login', target_type: 'admin_session', target_id: 'sess_01J8W0KQ7X9C1D-x1', reason: null, detail: { email: 'admin@licenseharbor.dev' }, ip: '203.0.113.10', user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)', created_at: NOW - 3 * 3600 }),
  auditEntry({ id: 'ae_06', actor_type: 'admin', actor_id: 'usr_admin', product_id: MOCK_PRODUCTS[0]!.id, license_id: null, action: 'product.update', target_type: 'product', target_id: MOCK_PRODUCTS[0]!.id, reason: null, detail: { name: 'AotoTokenPool' }, ip: '203.0.113.10', user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)', created_at: NOW - 6 * DAY }),
  auditEntry({ id: 'ae_07', actor_type: 'admin', actor_id: 'usr_admin', product_id: MOCK_PRODUCTS[0]!.id, license_id: null, action: 'code_batch.export', target_type: 'code_batch', target_id: 'cb_01J8W0KQ7X9C1D-b1', reason: null, detail: { rows: 114 }, ip: '203.0.113.10', user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)', created_at: NOW - 12 * DAY }),
  auditEntry({ id: 'ae_08', actor_type: 'admin', actor_id: 'usr_ops', product_id: MOCK_PRODUCTS[0]!.id, license_id: null, action: 'license.create', target_type: 'license', target_id: 'lic_01J8W11KQ7X3D50', reason: '手动补录，客户线下付款', detail: null, ip: '198.51.100.23', user_agent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)', created_at: NOW - 3 * DAY }),
  auditEntry({ id: 'ae_09', actor_type: 'admin', actor_id: 'usr_owner', product_id: MOCK_PRODUCTS[2]!.id, license_id: null, action: 'signing_key.compromise', target_type: 'signing_key', target_id: 'sk_01J8W0KQ7X9C1D-s5', reason: '私钥疑似泄露', detail: null, ip: '203.0.113.10', user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)', created_at: NOW - 190 * DAY }),
  auditEntry({ id: 'ae_10', actor_type: 'admin', actor_id: 'usr_admin', product_id: null, license_id: null, action: 'admin_user.create', target_type: 'admin_user', target_id: 'usr_ops', reason: null, detail: { email: 'ops@licenseharbor.dev' }, ip: '203.0.113.10', user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)', created_at: NOW - 200 * DAY }),
  auditEntry({ id: 'ae_11', actor_type: 'admin', actor_id: 'usr_admin', product_id: MOCK_PRODUCTS[0]!.id, license_id: 'lic_01J8W10KQ7XAA92', action: 'license.renew', target_type: 'license', target_id: 'lic_01J8W10KQ7XAA92', reason: null, detail: { expires_at: NOW + 6 * DAY }, ip: '203.0.113.10', user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)', created_at: NOW - 6 * DAY }),
  auditEntry({ id: 'ae_12', actor_type: 'admin', actor_id: 'usr_ops', product_id: MOCK_PRODUCTS[1]!.id, license_id: null, action: 'plan.update', target_type: 'plan', target_id: 'p-team-year', reason: null, detail: { max_devices: 20 }, ip: '198.51.100.23', user_agent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)', created_at: NOW - 21 * DAY }),
  // 失败事件（P-18）：客户端兑换失败写入 failure 审计（真实后端在 redeem catch 收口）
  auditEntry({ id: 'ae_13', actor_type: 'system', actor_id: null, product_id: MOCK_PRODUCTS[0]!.id, license_id: null, action: 'license_code.redeem', target_type: 'license_code', target_id: MOCK_PRODUCTS[0]!.id, reason: null, result: 'failure', detail: { error_code: 2004 }, ip: '198.51.100.66', user_agent: 'lh-node/0.1.0', created_at: NOW - 2 * 3600 }),
  auditEntry({ id: 'ae_14', actor_type: 'system', actor_id: null, product_id: MOCK_PRODUCTS[1]!.id, license_id: null, action: 'license_code.redeem', target_type: 'license_code', target_id: MOCK_PRODUCTS[1]!.id, reason: null, result: 'failure', detail: { error_code: 2006 }, ip: '198.51.100.77', user_agent: 'lh-python/0.1.0', created_at: NOW - 5 * 3600 }),
];

/* ── IAM（P-16/P-17/P-23）：管理员 / API Key / 会话 ── */

export const MOCK_ADMIN_USERS: AdminUser[] = [
  {
    id: 'usr_owner', email: 'owner@licenseharbor.dev', name: '韦优', role: 'owner',
    is_active: true, last_login_at: NOW - 20 * 60, created_at: NOW - 500 * DAY,
  },
  {
    id: 'usr_admin', email: 'admin@licenseharbor.dev', name: '运维值班', role: 'operator',
    is_active: true, last_login_at: NOW - 3 * 3600, created_at: NOW - 420 * DAY,
  },
  {
    id: 'usr_ops', email: 'ops@licenseharbor.dev', name: '售前支持', role: 'viewer',
    is_active: true, last_login_at: NOW - 2 * DAY, created_at: NOW - 200 * DAY,
  },
];

export const MOCK_API_KEYS: ApiKey[] = [
  {
    id: 'key_01J8W0KQ7X9C1D-p1', name: '发卡站兑付', key_prefix: 'lhk_A7K3M', role: 'operator',
    scopes: ['licenses:read', 'licenses:write', 'codes:read', 'customers:read'],
    last_used_at: NOW - 40 * 60, expires_at: null, created_at: NOW - 120 * DAY,
  },
  {
    id: 'key_01J8W0KQ7X9C1D-p2', name: 'CI 批量导入', key_prefix: 'lhk_2C9E', role: 'operator',
    scopes: ['products:read', 'plans:read', 'codes:read', 'codes:write', 'customers:read', 'customers:write'],
    last_used_at: NOW - 5 * DAY, expires_at: NOW + 30 * DAY, created_at: NOW - 60 * DAY,
  },
  {
    id: 'key_01J8W0KQ7X9C1D-p3', name: '自动续费桥接', key_prefix: 'lhk_7A1D', role: 'viewer',
    scopes: ['licenses:read', 'audit:read', 'stats:read'],
    last_used_at: NOW - 300 * DAY, expires_at: null, created_at: NOW - 400 * DAY,
  },
];

export const MOCK_SESSIONS: Session[] = [
  {
    id: 'sess_01J8W0KQ7X9C1D-x1', created_at: NOW - 3 * 3600, expires_at: NOW + 7 * DAY,
    last_seen_at: NOW - 40 * 60, ip: '203.0.113.10', user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)', is_current: true,
  },
  {
    id: 'sess_01J8W0KQ7X9C1D-x2', created_at: NOW - 2 * DAY, expires_at: NOW + 5 * DAY,
    last_seen_at: NOW - 2 * DAY, ip: '198.51.100.23', user_agent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)', is_current: false,
  },
  {
    id: 'sess_01J8W0KQ7X9C1D-x3', created_at: NOW - 20 * DAY, expires_at: NOW - 13 * DAY,
    last_seen_at: NOW - 20 * DAY, ip: '198.51.100.77', user_agent: 'curl/8.5.0', is_current: false,
  },
];
